import { createPortal } from 'react-dom';
import type { OfficerSummaryDto } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHO IS IN THIS CHIP, ON HOVER. [PRE-P6.1]
 *
 *  Three lines: name and rank, the phone in bold, and the officer's lineage. A filled chip
 *  shows a NAME and nothing else ([PRE-P6] §2d, because the rank was making it wide enough
 *  for the `⋮` to overlap the next slot) — this is where the rest of the identity went, and
 *  it is the same information the search sheet shows at pick time. Picking the wrong Sen out
 *  of forty is the likeliest data-entry error in this system, and after the pick the chip was
 *  the only place that error could be caught.
 *
 *  ⚠️⚠️ ONE CARD FOR THE WHOLE SCREEN, AND THAT IS THE WHOLE DESIGN — NOT AN AntD `Tooltip`.
 *
 *  Divergence #82 MEASURED this exact cost: `AddresseeCodes` wrapped every instance in a
 *  `Tooltip`, "each `Tooltip` mounts an rc-trigger and a `ResizeObserver`", and one per
 *  ALLOCATION ROW took `ArrangementBuilder.test.tsx` from 64.25s to 119.18s with three tests
 *  crossing the default timeout. A fill line renders up to twelve chips and a sheet renders
 *  several lines, so one per CHIP is that cost multiplied again. Only one can ever be visible,
 *  so only one is ever mounted: `FillLines` owns the hovered slot and renders this once.
 *
 *  ⚠️ THE REPO'S OWN ANSWER TO #82 WAS A NATIVE `title`, AND IT IS NOT AVAILABLE HERE. A
 *  native tooltip cannot bold a line, and the phone is the line an operator is reading.
 *
 *  ⚠️ IT IS NOT HOVER-ONLY. `FillLines` opens it on FOCUS of the chip's `⋮` as well as on
 *  pointer enter — hover is unavailable on touch and unreachable from a keyboard, which is
 *  [D4.3]'s argument against putting a real affordance behind one. `aria-describedby` on the
 *  trigger is what carries it to a screen reader; the id is a constant because there is only
 *  ever one card.
 *
 *  ⚠️ `position: fixed` AND A PORTAL, so an ancestor's `overflow` cannot clip it — the fill
 *  screen's `<main>` scrolls and `fill-scroll` clips. It is placed from the trigger's own
 *  `getBoundingClientRect()`, which jsdom reports as all-zero: the CONTENT is asserted in
 *  `DutyFill.test.tsx` and the RECTANGLE is asserted in `e2e/specs/fillGaps.spec.ts`, which
 *  is §8's standing division of labour rather than a gap.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** One card, so one id. `aria-describedby` on the trigger points here. */
export const OFFICER_HOVER_CARD_ID = 'officer-hover-card';

/** Enough room for three short lines; the card flips above the chip when it would not fit. */
const ESTIMATED_HEIGHT = 78;
const GAP = 8;
const MARGIN = 8;
/** Wide enough for a lineage, narrow enough not to cover the row it describes. */
const WIDTH = 260;

export type OfficerHoverCardProps = {
  officer: OfficerSummaryDto;
  /** The chip's rectangle, in viewport coordinates. */
  anchor: { top: number; left: number; width: number; height: number };
  /**
   * ⚠️ [amended C3c.1] THE VISIBLE HALF OF §7's "A WORD AS WELL AS A WASH".
   *
   * `SlotChip` carries the word `sr-only` and deliberately no native `title` — [PRE-P6.1]
   * removed that on the ground that a browser tooltip racing THIS card is two answers to one
   * gesture. So the card is where a sighted operator reads it, and this is the field.
   *
   * ⚠️ OPTIONAL, so a LIVE duty's card is byte-identical to what it always was — the same
   * reason `fillSlotSchema.attendance` is optional rather than nullable one layer down.
   */
  attendance?: string | undefined;
};

export const OfficerHoverCard = ({ officer, anchor, attendance }: OfficerHoverCardProps) => {
  /*
   * ⚠️ CLAMPED TO THE VIEWPORT ON BOTH AXES. A chip in the last grid column sits within a
   * card's width of the right edge, and a chip on the last visible row within its height of
   * the bottom — both are the ordinary case rather than the corner one, because the grid caps
   * at six columns and the sheet scrolls.
   */
  const below = anchor.top + anchor.height + GAP;
  const flips = below + ESTIMATED_HEIGHT + MARGIN > window.innerHeight;
  const top = flips ? Math.max(MARGIN, anchor.top - ESTIMATED_HEIGHT - GAP) : below;
  const left = Math.max(MARGIN, Math.min(anchor.left, window.innerWidth - WIDTH - MARGIN));

  return createPortal(
    <div
      id={OFFICER_HOVER_CARD_ID}
      role="tooltip"
      data-testid="officer-hover-card"
      /* ⚠️ `pointer-events-none`: the card is placed under the pointer's own path, and a card
         that could take the hover would flicker itself in and out. It is never interactive. */
      className="pointer-events-none fixed z-50 rounded-md border border-border bg-popover p-2.5 text-popover-foreground shadow-md"
      style={{ top, left, width: WIDTH }}
    >
      <p className="text-sm font-medium">
        {officer.name}
        {officer.rankShortName ? `, ${officer.rankShortName}` : ''}
      </p>
      {/* The phone is the line somebody is reading off the screen into a handset. */}
      <p className="text-sm font-bold tabular-nums" data-testid="officer-hover-phone">
        {officer.phone}
      </p>
      <p className="text-xs text-muted-foreground" data-testid="officer-hover-lineage">
        {/* ⚠️ `orgUnitName` IS THE FALLBACK, not a second rendering: an officer whose unit has
            no resolvable ancestry still has a posting, and printing nothing there would read
            as "unposted" rather than "unknown". */}
        {officer.orgUnitPath.length > 0 ? officer.orgUnitPath.join(' › ') : (officer.orgUnitName ?? '—')}
      </p>
      {/* ⚠️ LAST, so an elapsed duty ADDS a line rather than moving the three that were there —
          `ESTIMATED_HEIGHT` above is a flip threshold and not a claim about the content. */}
      {attendance === undefined ? null : (
        <p className="mt-1 text-xs font-medium" data-testid="officer-hover-attendance">
          {attendance}
        </p>
      )}
    </div>,
    document.body,
  );
};
